import { useLayoutEffect, useRef, type KeyboardEvent } from "react";
import { Box, Button, Flex } from "orcs-design-system";

import InboxEmpty from "./InboxEmpty";
import InboxListSkeleton from "./InboxListSkeleton";
import InboxListVirtualItems from "./InboxListVirtualItems";
import useInboxListVirtualizer from "./useInboxListVirtualizer";
import { type InboxItem } from "./registry";

const InboxList = ({
  items,
  selectedKey,
  arrivedKey = null,
  otherCount,
  hasMore,
  loadingMore,
  itemsLoading = false,
  onSelect,
  onLoadMore,
}: {
  items: InboxItem[];
  selectedKey: string | null;
  /** Key the auto-advance just opened; only that card gets the arrive pulse. */
  arrivedKey?: string | null;
  otherCount: number;
  hasMore: boolean;
  loadingMore: boolean;
  /** Items query in flight with no rows to show yet (first open or filter change). */
  itemsLoading?: boolean;
  onSelect: (key: string) => void;
  onLoadMore: () => void;
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const pendingFocusKey = useRef<string | null>(null);
  const selectedIndex = selectedKey
    ? items.findIndex((item) => item.key === selectedKey)
    : -1;

  const virtualizer = useInboxListVirtualizer(items, selectedIndex, parentRef);

  useLayoutEffect(() => {
    const key = pendingFocusKey.current;
    if (!key) {
      return;
    }
    const node = parentRef.current?.querySelector(
      `[data-testid="inbox-card-${CSS.escape(key)}"]`
    );
    if (!(node instanceof HTMLElement)) {
      return;
    }
    pendingFocusKey.current = null;
    node.focus();
  }, [selectedKey]);

  const onListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }
    if (items.length === 0) {
      return;
    }
    event.preventDefault();
    let next = 0;
    if (selectedIndex >= 0 && event.key === "ArrowDown") {
      next = Math.min(items.length - 1, selectedIndex + 1);
    } else if (selectedIndex >= 0) {
      next = Math.max(0, selectedIndex - 1);
    }
    const key = items[next]?.key;
    if (!key || key === selectedKey) {
      return;
    }
    pendingFocusKey.current = key;
    onSelect(key);
  };

  return (
    <Flex
      flexDirection="column"
      gap="s"
      width="100%"
      height="100%"
      minHeight={0}
    >
      {items.length === 0 && !hasMore && !itemsLoading ? (
        <InboxEmpty otherCount={otherCount} />
      ) : null}
      <Box
        ref={parentRef}
        flex="1 1 auto"
        minHeight={0}
        overflow="auto"
        onKeyDown={itemsLoading ? undefined : onListKeyDown}
      >
        {itemsLoading ? (
          <InboxListSkeleton />
        ) : (
          <InboxListVirtualItems
            items={items}
            selectedKey={selectedKey}
            arrivedKey={arrivedKey}
            onSelect={onSelect}
            virtualizer={virtualizer}
          />
        )}
        {hasMore && items.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            disabled={loadingMore}
            onClick={onLoadMore}
          >
            Load more
          </Button>
        ) : null}
      </Box>
    </Flex>
  );
};

export default InboxList;
